import { Spin } from "antd";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import * as yup from "yup";

import {
  IPasswordPolicy,
  ISetPasswordFormValues,
} from "../../@types/SetPassword";
import { Button } from "../../components/Button";
import { TextField } from "../../components/TextField";
import {
  FooterLinksDesktop,
  FooterLinksMobile,
} from "../../layout/components/FooterLinks";
import { authService } from "../../services/authService";
import { preLoginLogo } from "../../styles/preLoginBranding";
import { mixpanelActions } from "../../utils/mixpanel";
import { InvalidPassLinkPage } from "./InvalidPassLinkPage";
import {
  Logo,
  Container,
  StyledTile,
  createPasswordButtonStyles,
  passwordTextFieldStyles,
  InfoMessage,
  Form,
  LoadingOverlay,
  PasswordRequirementContainer,
  listStyles,
  confirmPasswordTextFieldStyles,
  ErrorMessage,
  spinCss,
} from "./SetPasswordPage.styles";

export function SetPasswordPage() {
  const { t } = useTranslation();

  const defaultPasswordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\^$*.[\]{}()?"!@#%&\\/,><'':;|_~`=+-]).{8,256}/;

  // Read inside the component, not at module scope: t() there would evaluate before i18next
  // initialises and freeze the first language for the life of the tab.
  const requiredPasswordValidationMessage = t(
    "setPassword_validation_required"
  );
  const passwordRequirementsValidationMessage = t(
    "setPassword_validation_criteria"
  );
  const passwordDoesntMatchValidationMessage = t(
    "setPassword_validation_mismatch"
  );

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isLinkValid, setIsLinkValid] = useState(false);
  const [isLoadingLinkValidation, setIsLoadingLinkValidation] = useState(true);

  const [isLoading, setIsLoading] = useState(false);
  const [isPatient, setIsPatient] = useState<boolean>(false);
  const [code, setCode] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [capsLockEnabled, setCapsLockEnabled] = useState(false);
  const [globalError, setGlobalError] = useState("");
  const [passwordPolicy, setPasswordPolicy] = useState({
    minimumLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSymbols: true,
  } as IPasswordPolicy);

  const fetchPasswordPolicy = async () => {
    setIsLoading(true);

    const policy = await authService.getPasswordPolicy();
    if (policy) {
      setPasswordPolicy(policy);
    }
    setIsLoading(false);
  };

  const verifyIfPassTokenIsValid = async (email: string, code: string) => {
    const { data } = await authService.validateTokenCode(email, code);

    if (data === true) {
      setIsLinkValid(true);
    }
    setIsLoadingLinkValidation(false);
  };

  useEffect(() => {
    mixpanelActions.track("Visited Screen: CreateNewPassword");

    const emailParam = searchParams.get("email");
    const codeParam = searchParams.get("code");
    const isPatient = searchParams.get("user") === "patient";
    setIsPatient(isPatient);
    if (codeParam && emailParam) {
      verifyIfPassTokenIsValid(emailParam, codeParam);
      setEmail(emailParam);
      setCode(codeParam);
      fetchPasswordPolicy();
    } else {
      navigate("/login");
    }
  }, []);

  const isSubmittingRef = useRef(false);

  const handleOnSubmitForm = async ({
    newPassword,
  }: ISetPasswordFormValues) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    mixpanelActions.track("User Action: SubmitNewPassword");

    setIsLoading(true);

    try {
      const result = await authService.setPassword({
        password: newPassword,
        email,
        code,
      });

      if (result.status >= 200 && result.status < 300) {
        let searchParams = {};
        if (result.data.isStaff === true) {
          searchParams = { isHcp: "true" };
        } else {
          searchParams = { isHcp: "false" };
        }
        navigate({
          pathname: "/set-password-success",
          search: createSearchParams(searchParams).toString(),
        });
      } else {
        setGlobalError(result.data.errors[0].message);
      }
    } catch (error) {
      console.error(error);
      setGlobalError(t("common_genericError"));
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const passwordRegex = passwordPolicy.regex
    ? new RegExp(passwordPolicy.regex)
    : defaultPasswordRegex;

  const formValidationSchema = yup.object().shape({
    newPassword: yup
      .string()
      .required(requiredPasswordValidationMessage)
      .matches(passwordRegex, passwordRequirementsValidationMessage),
    confirmNewPassword: yup
      .string()
      .required(requiredPasswordValidationMessage)
      .oneOf(
        [yup.ref("newPassword"), null],
        passwordDoesntMatchValidationMessage
      )
      .matches(passwordRegex, passwordRequirementsValidationMessage),
  });

  const formik = useFormik({
    initialValues: {
      newPassword: "",
      confirmNewPassword: "",
    },
    onSubmit: handleOnSubmitForm,
    validationSchema: formValidationSchema,
  });

  const handleCheckCapsLockOnKeyUp = (event: any) => {
    const capsEnabled =
      (event.getModifierState && event.getModifierState("CapsLock")) || false;

    setCapsLockEnabled(capsEnabled);
  };

  const shouldShowErrorMessage = (
    field: "newPassword" | "confirmNewPassword"
  ) => {
    return formik.touched[field] ? formik.errors[field] : "";
  };

  if (isLoadingLinkValidation) {
    return <Spin className={spinCss()} fullscreen size="large" />;
  }
  return isLinkValid ? (
    <Container>
      <StyledTile>
        {isLoading && <LoadingOverlay />}
        <Form onSubmit={formik.handleSubmit}>
          <Logo src={preLoginLogo.src} alt={preLoginLogo.alt} />
          <InfoMessage>{t("setPassword_title")}</InfoMessage>
          <PasswordRequirementContainer>
            <p>{t("setPassword_requirements_intro")} </p>
            <ul className={listStyles()}>
              <li>
                {t("setPassword_requirements_minLength", {
                  count: passwordPolicy.minimumLength,
                })}
              </li>
              {passwordPolicy.requireUppercase && (
                <li>{t("setPassword_requirements_uppercase")}</li>
              )}
              {passwordPolicy.requireLowercase && (
                <li>{t("setPassword_requirements_lowercase")}</li>
              )}
              {passwordPolicy.requireNumbers && (
                <li>{t("setPassword_requirements_number")}</li>
              )}
              {passwordPolicy.requireSymbols && (
                <li>{t("setPassword_requirements_symbol")}</li>
              )}
            </ul>
          </PasswordRequirementContainer>
          <div>
            <TextField
              isPassword
              name="newPassword"
              label={t("setPassword_newPasswordLabel")}
              placeholder="●●●●●●●●●●●●"
              value={formik.values.newPassword}
              onChange={formik.handleChange}
              className={passwordTextFieldStyles()}
              onKeyDown={handleCheckCapsLockOnKeyUp}
              errorMessage={shouldShowErrorMessage("newPassword")}
              warningMessage={
                capsLockEnabled ? t("login_capsLockOn") : undefined
              }
            />
            <TextField
              isPassword
              name="confirmNewPassword"
              label={t("setPassword_confirmPasswordLabel")}
              placeholder="●●●●●●●●●●●●"
              value={formik.values.confirmNewPassword}
              onChange={formik.handleChange}
              className={confirmPasswordTextFieldStyles()}
              onKeyDown={handleCheckCapsLockOnKeyUp}
              errorMessage={shouldShowErrorMessage("confirmNewPassword")}
              warningMessage={
                capsLockEnabled ? t("login_capsLockOn") : undefined
              }
            />
            {globalError && <ErrorMessage>{globalError}</ErrorMessage>}
            <Button
              type="submit"
              label={t("setPassword_submit")}
              isLoading={isLoading}
              className={createPasswordButtonStyles()}
            />
          </div>
        </Form>
        <FooterLinksMobile />
      </StyledTile>
      <FooterLinksDesktop />
    </Container>
  ) : (
    <InvalidPassLinkPage isPatient={isPatient} email={email} />
  );
}
